import React, { useEffect, useState } from "react";
import { Home, Search, X } from "lucide-react";
import { fetchCampaignProperties } from "../api/communicationsApi";

/**
 * "Populate from a property": search your listings, pick one, and the composer
 * fills itself in from the real record - address, photos, features, price, the
 * listing agent.
 *
 * Extracted from the Campaign editor so the Find Buyers composer can have the
 * same section rather than a lookalike (CG, Monday 12887883570, 2026-09-01: "we
 * need to add the search a property section which will pull all the info of the
 * property. Please make it the same. Likewise with the text function"). Both
 * pages now render this one component.
 *
 * It owns the search box and nothing else. Picking hands the caller the listing
 * ROW, not the full record: each composer then loads the email-data payload
 * itself and decides what to write where - and the Campaign editor needs that
 * same load for its "?template=just_listed" hand-off, where no row was ever
 * clicked.
 */
export default function PropertyPickerPanel({
  selected = null,
  onPick,
  onClear,
  busy = false,
  blurb,
  className = "",
  // Opt-in multi-select: pick several listings, each rendered as its own card
  // (Michael Piccoli, Web Dev Team, 2026-09-23). Left off by default so the
  // single-select callers (e.g. Find Buyers) are unchanged. When on, pass the
  // chosen listings as `selectedItems` and handle `onRemove(id)`.
  multiple = false,
  selectedItems = [],
  onRemove,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced so typing an address does not fire a request per keystroke.
  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      fetchCampaignProperties(query.trim())
        .then((r) => setResults(r?.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  const clear = () => {
    setQuery("");
    setResults([]);
    onClear && onClear();
  };

  // In multi mode a pick keeps the search open (clearing the box) so the agent
  // can add another; in single mode it closes, as before.
  const pick = (r) => {
    onPick && onPick(r);
    if (multiple) {
      setQuery("");
    } else {
      setOpen(false);
    }
  };

  const chosenIds = new Set((selectedItems || []).map((s) => String(s.id)));
  const visibleResults = multiple
    ? results.filter((r) => !chosenIds.has(String(r.id)))
    : results;

  const searchBox = (
    <div className="relative">
      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        /* Closed on a timer, not on blur alone: the result rows are inside
           this box, so blurring straight to a click would unmount the row
           out from under the mouse. */
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={multiple ? "Search to add another listing" : "Search your listings by address or suburb"}
        aria-label="Search your listings by address or suburb"
        className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm bg-white"
      />
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {busy || loading ? (
            <div className="px-3 py-3 text-sm text-gray-400">Loading…</div>
          ) : visibleResults.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-400">
              {query.trim() ? "No matching listings." : "Start typing to find a listing."}
            </div>
          ) : (
            visibleResults.map((r) => (
              <button
                key={r.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
              >
                {r.thumbnail ? (
                  <img src={r.thumbnail} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                    <Home className="w-4 h-4 text-gray-300" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{r.address}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {[r.suburb, r.price_label, r.status].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );

  const selectedRow = (item, onRemoveItem) => (
    <div key={item.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-2">
      {item.images?.[0] || item.thumbnail ? (
        <img
          src={item.images?.[0] || item.thumbnail}
          alt=""
          className="w-14 h-14 rounded-md object-cover shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
          <Home className="w-5 h-5 text-gray-300" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-800 truncate">{item.address}</p>
        <p className="text-xs text-gray-400 truncate">
          {[item.suburb, item.price_label].filter(Boolean).join(" · ")}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemoveItem}
        className="text-xs font-bold text-gray-500 hover:text-red-600 inline-flex items-center gap-1 shrink-0"
        title="Remove property"
      >
        <X className="w-3.5 h-3.5" /> Remove
      </button>
    </div>
  );

  return (
    <div className={`rounded-lg border border-[#bfeef2] bg-[#f6fdfe] p-3 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <Home className="w-4 h-4 text-[#25AFC0]" />
        <p className="font-bold text-gray-800 text-sm">Populate from a property</p>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        {blurb || (multiple ? (
          <>
            Pick one or more of your listings and we'll drop each one into the email as its own
            card (address, photos, details, price) - ideal for a multi-listing send. You can keep
            editing after.
          </>
        ) : (
          <>
            Pick one of your listings and we'll drop in its address, photos, details, price and
            the listing agent - ideal for a <strong>Just Listed</strong> message. You can keep
            editing after.
          </>
        ))}
      </p>

      {multiple ? (
        <div className="space-y-2">
          {selectedItems.length > 0 && (
            <div className="space-y-2">
              {selectedItems.map((item) => selectedRow(item, () => onRemove && onRemove(item.id)))}
            </div>
          )}
          {searchBox}
        </div>
      ) : selected ? (
        selectedRow(selected, clear)
      ) : (
        searchBox
      )}
    </div>
  );
}
